import { useQuery } from '@tanstack/react-query';
import { getMyTutorAnalytics } from '../../../tutor/api/tutorAnalyticsApi';
import type { TutorAnalyticsSummary } from '../../../tutor/types/tutorAnalytics';
import { useFeatureAccess } from '../../../contexts/FeatureAccessContext';
import { useTutorQueryIdentity } from '../../tutor/hooks/useTutorQueryIdentity';
import { tutorAnalyticsSummaryQueryKey } from '../queryKeys';
import {
  isDashboardIdentityReady,
} from '../utils/dashboardQueryGating';

const STALE_TIME_MS = 5 * 60 * 1000;

function normalizeTutorAnalyticsSummary(result: TutorAnalyticsSummary): TutorAnalyticsSummary {
  return {
    ok: result.ok || true,
    total_tutor_minutes: result.total_tutor_minutes || 0,
    sessions_count: result.sessions_count || 0,
    last_active_at: result.last_active_at || null,
    latest_speaking_level: result.latest_speaking_level || null,
    latest_overall_score: result.latest_overall_score || null,
    trend: result.trend || [],
    last_lesson_no: result.last_lesson_no || null,
    last_topic_label: result.last_topic_label || null,
    speaking_sessions: result.speaking_sessions || [],
    daily_engagement: result.daily_engagement || [],
    session_history: result.session_history || [],
  };
}

function mapTutorAnalyticsError(err: unknown): string {
  const response = (err as { response?: { status?: number } })?.response;
  if (response?.status === 401) return 'Please login again';
  if (response?.status === 403) {
    return 'Access denied - analytics may not be available for your role';
  }
  if (response?.status === 400) return 'Tenant missing or invalid request';
  if (response?.status === 404) {
    return 'Analytics service not found - feature may still be in development';
  }
  if (response?.status === 500) {
    return 'Server error - analytics service may be temporarily unavailable';
  }
  if (!response) return 'Cannot connect to analytics service';
  return 'Failed to load analytics';
}

/** Canonical owner for GET /v1/tutor/analytics/me/summary on the student dashboard. */
export function useTutorAnalyticsSummary(days: number = 30, options?: { enabled?: boolean }) {
  const { isSchoolFeatureEnabled } = useFeatureAccess();
  const { membershipKey, studentKey } = useTutorQueryIdentity();
  const identityReady = isDashboardIdentityReady(membershipKey, studentKey);

  const enabled =
    (options?.enabled ?? true) && isSchoolFeatureEnabled('ai_tutor') && identityReady;

  const queryKey = identityReady
    ? tutorAnalyticsSummaryQueryKey({ membershipKey, studentKey, days })
    : (['student-dashboard', 'tutor-analytics-summary', 'disabled', days] as const);

  const query = useQuery<TutorAnalyticsSummary, Error>({
    queryKey,
    queryFn: async () => {
      const result = await getMyTutorAnalytics(days);
      return normalizeTutorAnalyticsSummary(result);
    },
    enabled,
    staleTime: STALE_TIME_MS,
  });

  return {
    data: query.data ?? null,
    loading: enabled && query.isLoading,
    error: query.error ? mapTutorAnalyticsError(query.error) : null,
    refetch: () => void query.refetch(),
  };
}

export { normalizeTutorAnalyticsSummary, mapTutorAnalyticsError };
